import { useCallback } from 'react'
import { ReceptorUtilPromiseClient } from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_grpc_web_pb'
import {
  CheckReceptorFileAccessRequest,
  CheckReceptorFileAccessResponse,
} from '@trustero/trustero-api-web/lib/receptorutil/receptorutil_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'

/**
 * The Google Picker runs under the signed-in user's Google account, while the
 * backend reads Drive with the receptor's service account. These helpers ask
 * the backend whether the receptor can actually read what the user picked.
 */

export type ReceptorAccessSplit<T extends { id: string }> = {
  accessible: T[]
  denied: T[]
  /** Identity the receptor uses against the provider, e.g. a service account email. */
  receptorIdentity: string
}

export const useCheckReceptorFileAccess = (): ((
  receptorId: string,
  fileIds: string[],
) => Promise<CheckReceptorFileAccessResponse>) => {
  const client = useAuthorizedGrpcClient(ReceptorUtilPromiseClient)
  return useCallback(
    async (receptorId: string, fileIds: string[]) => {
      const request = new CheckReceptorFileAccessRequest()
        .setReceptorId(receptorId)
        .setFileIdsList(fileIds)
      return client.checkReceptorFileAccess(request)
    },
    [client],
  )
}

/**
 * Splits picked documents into those the receptor can read and those it was
 * denied on. Items whose check was inconclusive (transient provider error)
 * come back with accessible=true from the backend and are kept.
 */
export const splitByReceptorAccess = <T extends { id: string }>(
  docs: T[],
  response: CheckReceptorFileAccessResponse,
): ReceptorAccessSplit<T> => {
  const deniedIds = new Set(
    response
      .getResultsList()
      .filter((result) => !result.getAccessible())
      .map((result) => result.getFileId()),
  )
  return {
    accessible: docs.filter((doc) => !deniedIds.has(doc.id)),
    denied: docs.filter((doc) => deniedIds.has(doc.id)),
    receptorIdentity: response.getReceptorIdentity(),
  }
}

const MAX_NAMES_IN_MESSAGE = 3

export const buildDeniedAccessMessage = (
  denied: Array<{ name?: string; id: string }>,
  receptorIdentity: string,
): string => {
  const names = denied.map((doc) => doc.name || doc.id)
  const shown = names.slice(0, MAX_NAMES_IN_MESSAGE).join(', ')
  const rest = names.length - MAX_NAMES_IN_MESSAGE
  const list = rest > 0 ? `${shown} and ${rest} more` : shown
  const who = receptorIdentity
    ? `with ${receptorIdentity}`
    : 'with the Trustero Google Drive account'
  return `Trustero cannot read ${
    denied.length === 1 ? 'this item' : `${denied.length} of the selected items`
  } (${list}). Share ${
    denied.length === 1 ? 'it' : 'them'
  } ${who} in Google Drive, then pick again.`
}
